/**
 * PhoneBookingTab.js - Manual Booking Entry for Phone Orders
 * Allows owner to create bookings when receiving calls from customers
 * Enhanced with service-specific fields, auto-pricing, and card payment over phone
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, User, MapPin, Calendar, Clock, DollarSign, 
  Car, Package, ShoppingBag, Shirt, Save, Plus, Trash2,
  CheckCircle, AlertCircle, FileText, CreditCard, Banknote,
  Calculator, Shield, AlertTriangle, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../services/api';

const SERVICE_TYPES = [
  { id: 'rides', label: 'Rides & Transportation', icon: Car, color: '#8B5CF6' },
  { id: 'courier', label: 'Courier & Delivery', icon: Package, color: '#3B82F6' },
  { id: 'errands', label: 'Errands & Tasks', icon: ShoppingBag, color: '#EC4899' },
  { id: 'laundry', label: 'Laundry Service', icon: Shirt, color: '#06B6D4' }
];

// Pricing configuration
const PRICING = {
  rides: {
    base_fare: 8.00,
    per_mile: 2.50,
    per_minute_wait: 0.35,
    airport_fee: 15.00,
    after_hours_multiplier: 1.5, // 10 PM - 6 AM
  },
  courier: {
    base_fare: 12.00,
    per_mile: 1.75,
    heavy_item_fee: 10.00, // Over 30 lbs
    fragile_fee: 5.00,
    express_fee: 15.00, // Same day rush
  },
  errands: {
    hourly_rate: 25.00,
    grocery_fee: 10.00,
    prescription_fee: 8.00,
    multi_stop_fee: 5.00, // Per additional stop
  },
  laundry: {
    per_load_wash_dry_fold: 25.00,
    per_load_wash_only: 15.00,
    per_load_dry_only: 12.00,
    dry_cleaning_item: 8.00,
    express_fee: 15.00, // Same day
    pickup_delivery_fee: 10.00,
  }
};

const PhoneBookingTab = ({ workers = [], onBookingCreated }) => {
  const [loading, setLoading] = useState(false);
  const [showCardPayment, setShowCardPayment] = useState(false);
  const [cardPermissionConfirmed, setCardPermissionConfirmed] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  
  const [formData, setFormData] = useState({
    // Customer Info
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    
    // Service Details
    service_type: '',
    service_name: '',
    
    // Location
    pickup_address: '',
    pickup_city: '',
    pickup_zip: '',
    delivery_address: '',
    delivery_city: '',
    delivery_zip: '',
    estimated_miles: 0,
    
    // Scheduling
    scheduled_date: '',
    scheduled_time: '',
    estimated_hours: 1,
    
    // Service-Specific Details
    // RIDES
    ride_type: 'standard', // standard, airport, medical
    num_passengers: 1,
    wait_time_minutes: 0,
    round_trip: false,
    
    // COURIER
    package_weight: 'light', // light, medium, heavy
    is_fragile: false,
    is_express: false,
    package_description: '',
    
    // ERRANDS
    errand_type: 'general', // general, grocery, prescription, returns
    num_stops: 1,
    shopping_list: '',
    estimated_spend: 0,
    
    // LAUNDRY
    num_loads: 1,
    laundry_service: 'wash_dry_fold', // wash_dry_fold, wash_only, dry_only
    dry_cleaning_items: 0,
    is_express_laundry: false,
    include_pickup_delivery: true,
    special_care_items: '',
    
    // Pricing
    quoted_price: '',
    use_calculated_price: true,
    payment_method: 'pending', // pending, cash, card
    
    // Card Payment Details
    card_last_four: '',
    card_holder_name: '',
    verbal_permission_script_read: false,
    
    // Assignment
    assigned_worker_id: '',
    
    // Notes
    special_instructions: '',
    internal_notes: ''
  });

  const [recentBookings, setRecentBookings] = useState([]);

  // Calculate price based on service type and details
  const calculatedPrice = useMemo(() => {
    if (!formData.service_type) return { total: 0, breakdown: [] };
    
    const breakdown = [];
    let total = 0;
    const pricing = PRICING[formData.service_type];
    
    switch (formData.service_type) {
      case 'rides': {
        // Base fare
        breakdown.push({ label: 'Base Fare', amount: pricing.base_fare });
        total += pricing.base_fare;
        
        // Mileage
        const miles = parseFloat(formData.estimated_miles) || 0;
        if (miles > 0) {
          const mileageCost = miles * pricing.per_mile;
          breakdown.push({ label: `${miles} miles @ $${pricing.per_mile}/mi`, amount: mileageCost });
          total += mileageCost;
        }
        
        // Wait time
        const waitMin = parseInt(formData.wait_time_minutes) || 0;
        if (waitMin > 0) {
          const waitCost = waitMin * pricing.per_minute_wait;
          breakdown.push({ label: `${waitMin} min wait @ $${pricing.per_minute_wait}/min`, amount: waitCost });
          total += waitCost;
        }
        
        // Airport fee
        if (formData.ride_type === 'airport') {
          breakdown.push({ label: 'Airport Fee', amount: pricing.airport_fee });
          total += pricing.airport_fee;
        }
        
        // Round trip
        if (formData.round_trip) {
          const roundTripExtra = total * 0.8; // 80% of one-way for return
          breakdown.push({ label: 'Round Trip (80%)', amount: roundTripExtra });
          total += roundTripExtra;
        }
        
        // After hours check
        const hour = parseInt(formData.scheduled_time?.split(':')[0]) || 12;
        if (hour >= 22 || hour < 6) {
          const afterHoursExtra = total * (pricing.after_hours_multiplier - 1);
          breakdown.push({ label: 'After Hours (+50%)', amount: afterHoursExtra });
          total += afterHoursExtra;
        }
        break;
      }
      
      case 'courier': {
        // Base fare
        breakdown.push({ label: 'Base Fare', amount: pricing.base_fare });
        total += pricing.base_fare;
        
        // Mileage
        const miles = parseFloat(formData.estimated_miles) || 0;
        if (miles > 0) {
          const mileageCost = miles * pricing.per_mile;
          breakdown.push({ label: `${miles} miles @ $${pricing.per_mile}/mi`, amount: mileageCost });
          total += mileageCost;
        }
        
        // Heavy item
        if (formData.package_weight === 'heavy') {
          breakdown.push({ label: 'Heavy Item Fee (30+ lbs)', amount: pricing.heavy_item_fee });
          total += pricing.heavy_item_fee;
        }
        
        // Fragile
        if (formData.is_fragile) {
          breakdown.push({ label: 'Fragile Handling', amount: pricing.fragile_fee });
          total += pricing.fragile_fee;
        }
        
        // Express
        if (formData.is_express) {
          breakdown.push({ label: 'Express/Rush Delivery', amount: pricing.express_fee });
          total += pricing.express_fee;
        }
        break;
      }
      
      case 'errands': {
        // Hourly rate
        const hours = parseFloat(formData.estimated_hours) || 1;
        const hourlyCost = hours * pricing.hourly_rate;
        breakdown.push({ label: `${hours} hours @ $${pricing.hourly_rate}/hr`, amount: hourlyCost });
        total += hourlyCost;
        
        // Errand type fees
        if (formData.errand_type === 'grocery') {
          breakdown.push({ label: 'Grocery Shopping Fee', amount: pricing.grocery_fee });
          total += pricing.grocery_fee;
        } else if (formData.errand_type === 'prescription') {
          breakdown.push({ label: 'Prescription Pickup Fee', amount: pricing.prescription_fee });
          total += pricing.prescription_fee;
        }
        
        // Multi-stop fee
        const stops = parseInt(formData.num_stops) || 1;
        if (stops > 1) {
          const multiStopCost = (stops - 1) * pricing.multi_stop_fee;
          breakdown.push({ label: `${stops - 1} additional stops @ $${pricing.multi_stop_fee}`, amount: multiStopCost });
          total += multiStopCost;
        }
        break;
      }
      
      case 'laundry': {
        const loads = parseInt(formData.num_loads) || 1;
        
        // Service type
        let perLoadRate = pricing.per_load_wash_dry_fold;
        let serviceLabel = 'Wash, Dry & Fold';
        if (formData.laundry_service === 'wash_only') {
          perLoadRate = pricing.per_load_wash_only;
          serviceLabel = 'Wash Only';
        } else if (formData.laundry_service === 'dry_only') {
          perLoadRate = pricing.per_load_dry_only;
          serviceLabel = 'Dry Only';
        }
        
        const laundryBaseCost = loads * perLoadRate;
        breakdown.push({ label: `${loads} load(s) - ${serviceLabel} @ $${perLoadRate}`, amount: laundryBaseCost });
        total += laundryBaseCost;
        
        // Dry cleaning items
        const dryCleanItems = parseInt(formData.dry_cleaning_items) || 0;
        if (dryCleanItems > 0) {
          const dryCleanCost = dryCleanItems * pricing.dry_cleaning_item;
          breakdown.push({ label: `${dryCleanItems} dry cleaning items @ $${pricing.dry_cleaning_item}`, amount: dryCleanCost });
          total += dryCleanCost;
        }
        
        // Express
        if (formData.is_express_laundry) {
          breakdown.push({ label: 'Express Service (Same Day)', amount: pricing.express_fee });
          total += pricing.express_fee;
        }
        
        // Pickup & Delivery
        if (formData.include_pickup_delivery) {
          breakdown.push({ label: 'Pickup & Delivery', amount: pricing.pickup_delivery_fee });
          total += pricing.pickup_delivery_fee;
        }
        break;
      }
      
      default:
        break;
    }
    
    return { total: Math.round(total * 100) / 100, breakdown };
  }, [formData.service_type, formData.estimated_miles, formData.wait_time_minutes, formData.ride_type, 
      formData.round_trip, formData.scheduled_time, formData.package_weight, formData.is_fragile,
      formData.is_express, formData.estimated_hours, formData.errand_type, formData.num_stops,
      formData.num_loads, formData.laundry_service, formData.dry_cleaning_items, formData.is_express_laundry,
      formData.include_pickup_delivery]);

  // Update quoted price when calculation changes - only when use_calculated_price is true
  useEffect(() => {
    if (formData.use_calculated_price && calculatedPrice.total > 0) {
      // Use functional update to avoid dependency on formData
      setFormData(prev => {
        // Only update if the price is actually different to avoid loops
        const newPrice = calculatedPrice.total.toFixed(2);
        if (prev.quoted_price !== newPrice) {
          return { ...prev, quoted_price: newPrice };
        }
        return prev;
      });
    }
  }, [calculatedPrice.total, formData.use_calculated_price]);

  // Load recent phone bookings
  useEffect(() => {
    loadRecentBookings();
  }, []);

  const loadRecentBookings = async () => {
    try {
      const response = await api.get('/bookings', { 
        params: { 
          source: 'phone',
          limit: 5,
          sort: '-created_at'
        } 
      });
      // Handle both array and object with bookings key
      const data = Array.isArray(response.data) ? response.data : (response.data?.bookings || []);
      // Filter for phone bookings on client side as fallback
      const phoneBookings = data.filter(b => b.source === 'phone' || b.booking_type === 'phone_order');
      setRecentBookings(phoneBookings.slice(0, 5));
    } catch (error) {
      console.error('Failed to load recent bookings:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error('Please enter customer name and phone number');
      return;
    }
    if (!formData.service_type) {
      toast.error('Please select a service type');
      return;
    }
    if (!formData.scheduled_date || !formData.scheduled_time) {
      toast.error('Please select date and time');
      return;
    }
    if (!formData.pickup_address) {
      toast.error('Please enter the service address');
      return;
    }

    setLoading(true);
    try {
      // Build service-specific details
      let serviceDetails = {};
      
      if (formData.service_type === 'rides') {
        serviceDetails = {
          ride_type: formData.ride_type,
          num_passengers: parseInt(formData.num_passengers) || 1,
          wait_time_minutes: parseInt(formData.wait_time_minutes) || 0,
          round_trip: formData.round_trip,
          estimated_miles: parseFloat(formData.estimated_miles) || 0,
        };
      } else if (formData.service_type === 'courier') {
        serviceDetails = {
          package_weight: formData.package_weight,
          package_description: formData.package_description,
          is_fragile: formData.is_fragile,
          is_express: formData.is_express,
          estimated_miles: parseFloat(formData.estimated_miles) || 0,
        };
      } else if (formData.service_type === 'errands') {
        serviceDetails = {
          errand_type: formData.errand_type,
          num_stops: parseInt(formData.num_stops) || 1,
          shopping_list: formData.shopping_list,
          estimated_spend: parseFloat(formData.estimated_spend) || 0,
        };
      } else if (formData.service_type === 'laundry') {
        serviceDetails = {
          num_loads: parseInt(formData.num_loads) || 1,
          laundry_service: formData.laundry_service,
          dry_cleaning_items: parseInt(formData.dry_cleaning_items) || 0,
          is_express_laundry: formData.is_express_laundry,
          include_pickup_delivery: formData.include_pickup_delivery,
          special_care_items: formData.special_care_items,
        };
      }

      const bookingData = {
        // Mark as phone booking
        source: 'phone',
        booking_type: 'phone_order',
        
        // Customer
        guest_name: formData.customer_name,
        guest_phone: formData.customer_phone,
        guest_email: formData.customer_email || null,
        
        // Service
        service_type: formData.service_type,
        service_name: formData.service_name || SERVICE_TYPES.find(s => s.id === formData.service_type)?.label,
        service_details: serviceDetails,
        
        // Location
        address: formData.pickup_address,
        city: formData.pickup_city,
        state: 'NC',
        zip_code: formData.pickup_zip,
        delivery_address: formData.delivery_address || null,
        delivery_city: formData.delivery_city || null,
        delivery_zip: formData.delivery_zip || null,
        estimated_miles: parseFloat(formData.estimated_miles) || 0,
        
        // Schedule
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        duration_hours: parseFloat(formData.estimated_hours) || 1,
        
        // Pricing
        total_amount: parseFloat(formData.quoted_price) || 0,
        price_breakdown: calculatedPrice.breakdown,
        payment_method: formData.payment_method,
        payment_status: formData.payment_method === 'cash' ? 'cash_pending' : (formData.payment_method === 'card' ? 'card_pending' : 'pending'),
        
        // Card payment info (if applicable)
        card_payment_info: formData.payment_method === 'card' ? {
          card_holder_name: formData.card_holder_name,
          card_last_four: formData.card_last_four,
          verbal_permission_confirmed: formData.verbal_permission_script_read,
          permission_timestamp: formData.verbal_permission_script_read ? new Date().toISOString() : null,
        } : null,
        
        // Assignment
        assigned_worker_id: formData.assigned_worker_id || null,
        status: formData.assigned_worker_id ? 'assigned' : 'pending',
        
        // Notes
        special_instructions: formData.special_instructions,
        internal_notes: `[PHONE BOOKING] ${formData.internal_notes || 'Booked via phone call'}`,
        
        // Timestamps
        created_at: new Date().toISOString()
      };

      const response = await api.post('/bookings/phone-order', bookingData);
      
      // Show success with reference number
      const refNum = response.data?.reference_number || response.data?.id;
      const bookingId = response.data?.id;
      
      // If card payment selected and customer has email, send payment link
      if (formData.payment_method === 'card' && formData.customer_email) {
        try {
          toast.loading('Sending payment link to customer...', { id: 'payment-link' });
          const linkResponse = await api.post('/checkout/send-payment-link', {
            booking_id: bookingId,
            send_via: 'email'
          });
          toast.success(
            <div className="flex flex-col gap-1">
              <span className="font-semibold">Payment Link Sent!</span>
              <span className="text-sm opacity-90">{linkResponse.data?.message || 'Customer will receive the link via email'}</span>
            </div>,
            { id: 'payment-link', duration: 6000 }
          );
        } catch (linkError) {
          console.error('Payment link error:', linkError);
          toast.warning(
            <div className="flex flex-col gap-1">
              <span className="font-semibold">Booking Created!</span>
              <span className="text-sm opacity-90">Payment link could not be sent automatically. Send manually from booking details.</span>
            </div>,
            { id: 'payment-link', duration: 6000 }
          );
        }
      }
      
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">Phone Booking Created!</span>
          <span className="text-sm opacity-90">Reference: {refNum}</span>
        </div>,
        { duration: 5000 }
      );
      
      // Reset form
      setFormData({
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        service_type: '',
        service_name: '',
        pickup_address: '',
        pickup_city: '',
        pickup_zip: '',
        delivery_address: '',
        delivery_city: '',
        delivery_zip: '',
        estimated_miles: 0,
        scheduled_date: '',
        scheduled_time: '',
        estimated_hours: 1,
        // Rides
        ride_type: 'standard',
        num_passengers: 1,
        wait_time_minutes: 0,
        round_trip: false,
        // Courier
        package_weight: 'light',
        is_fragile: false,
        is_express: false,
        package_description: '',
        // Errands
        errand_type: 'general',
        num_stops: 1,
        shopping_list: '',
        estimated_spend: 0,
        // Laundry
        num_loads: 1,
        laundry_service: 'wash_dry_fold',
        dry_cleaning_items: 0,
        is_express_laundry: false,
        include_pickup_delivery: true,
        special_care_items: '',
        // Pricing
        quoted_price: '',
        use_calculated_price: true,
        payment_method: 'pending',
        // Card
        card_last_four: '',
        card_holder_name: '',
        verbal_permission_script_read: false,
        // Other
        assigned_worker_id: '',
        special_instructions: '',
        internal_notes: ''
      });
      
      // Reset card payment state
      setShowCardPayment(false);
      setCardPermissionConfirmed(false);
      setShowPriceBreakdown(false);
      
      // Refresh recent bookings
      loadRecentBookings();
      
      // Callback
      if (onBookingCreated) {
        onBookingCreated(response.data);
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const selectedService = SERVICE_TYPES.find(s => s.id === formData.service_type);

  // Get available workers for selected service
  const availableWorkers = workers.filter(w => 
    w.is_verified && 
    w.service_types?.includes(formData.service_type)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#F4C2C2]/20 flex items-center justify-center border border-[#D4AF37]/30">
            <Phone className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--text-primary)]">Phone Booking Entry</h2>
            <p className="text-sm text-[var(--text-muted)]">Create bookings from phone calls</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="card p-6 space-y-6">
            {/* Customer Information */}
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-[#D4AF37]" />
                Customer Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    placeholder="Customer name"
                    className="mt-1"
                    data-testid="phone-booking-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input
                    value={formData.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                    data-testid="phone-booking-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs">Email (optional)</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Service Selection */}
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                Service Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {SERVICE_TYPES.map(service => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => handleChange('service_type', service.id)}
                    className={`p-4 rounded-xl border text-center transition-all ${
                      formData.service_type === service.id
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-[var(--border)] hover:border-[var(--border-primary)]'
                    }`}
                    data-testid={`service-${service.id}`}
                  >
                    <service.icon className="w-6 h-6 mx-auto mb-2" style={{ color: service.color }} />
                    <p className="text-xs font-medium">{service.label}</p>
                  </button>
                ))}
              </div>
              
              <div>
                <Label className="text-xs">Service Description</Label>
                <Input
                  value={formData.service_name}
                  onChange={(e) => handleChange('service_name', e.target.value)}
                  placeholder={selectedService ? `e.g., ${selectedService.label}` : 'Describe the service'}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#D4AF37]" />
                Service Location
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs">Address *</Label>
                  <Input
                    value={formData.pickup_address}
                    onChange={(e) => handleChange('pickup_address', e.target.value)}
                    placeholder="Street address"
                    className="mt-1"
                    data-testid="phone-booking-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input
                      value={formData.pickup_city}
                      onChange={(e) => handleChange('pickup_city', e.target.value)}
                      placeholder="City"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">ZIP</Label>
                    <Input
                      value={formData.pickup_zip}
                      onChange={(e) => handleChange('pickup_zip', e.target.value)}
                      placeholder="27886"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Delivery Address (for courier/errands) */}
              {['courier', 'errands', 'laundry'].includes(formData.service_type) && (
                <div className="mt-4 p-4 rounded-lg bg-[var(--background)] border border-[var(--border)]">
                  <Label className="text-xs text-[var(--text-muted)]">Delivery/Drop-off Address</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                    <div className="md:col-span-2">
                      <Input
                        value={formData.delivery_address}
                        onChange={(e) => handleChange('delivery_address', e.target.value)}
                        placeholder="Delivery street address"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={formData.delivery_city}
                        onChange={(e) => handleChange('delivery_city', e.target.value)}
                        placeholder="City"
                      />
                      <Input
                        value={formData.delivery_zip}
                        onChange={(e) => handleChange('delivery_zip', e.target.value)}
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SERVICE-SPECIFIC DETAILS */}
            <AnimatePresence mode="wait">
              {formData.service_type && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 rounded-xl bg-gradient-to-r from-[var(--background)] to-[var(--background-subtle)] border border-[var(--border)]">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                      {selectedService && <selectedService.icon className="w-4 h-4" style={{ color: selectedService.color }} />}
                      {selectedService?.label} Details
                    </h3>

                    {/* RIDES DETAILS */}
                    {formData.service_type === 'rides' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs">Ride Type</Label>
                            <Select value={formData.ride_type} onValueChange={(v) => handleChange('ride_type', v)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="standard">Standard</SelectItem>
                                <SelectItem value="airport">Airport</SelectItem>
                                <SelectItem value="medical">Medical Appt</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Est. Miles</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.estimated_miles}
                              onChange={(e) => handleChange('estimated_miles', e.target.value)}
                              placeholder="0"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Passengers</Label>
                            <Input
                              type="number"
                              min="1"
                              max="6"
                              value={formData.num_passengers}
                              onChange={(e) => handleChange('num_passengers', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Wait Time (min)</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formData.wait_time_minutes}
                              onChange={(e) => handleChange('wait_time_minutes', e.target.value)}
                              placeholder="0"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.round_trip}
                              onCheckedChange={(checked) => handleChange('round_trip', checked)}
                            />
                            <span className="text-sm">Round Trip</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* COURIER DETAILS */}
                    {formData.service_type === 'courier' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs">Package Weight</Label>
                            <Select value={formData.package_weight} onValueChange={(v) => handleChange('package_weight', v)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="light">Light (under 10 lbs)</SelectItem>
                                <SelectItem value="medium">Medium (10-30 lbs)</SelectItem>
                                <SelectItem value="heavy">Heavy (30+ lbs)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Est. Miles</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.1"
                              value={formData.estimated_miles}
                              onChange={(e) => handleChange('estimated_miles', e.target.value)}
                              placeholder="0"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Package Description</Label>
                            <Input
                              value={formData.package_description}
                              onChange={(e) => handleChange('package_description', e.target.value)}
                              placeholder="Documents, box, etc."
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.is_fragile}
                              onCheckedChange={(checked) => handleChange('is_fragile', checked)}
                            />
                            <span className="text-sm">Fragile Item</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.is_express}
                              onCheckedChange={(checked) => handleChange('is_express', checked)}
                            />
                            <span className="text-sm">Express/Rush (+$15)</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {/* ERRANDS DETAILS */}
                    {formData.service_type === 'errands' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs">Errand Type</Label>
                            <Select value={formData.errand_type} onValueChange={(v) => handleChange('errand_type', v)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="general">General Errands</SelectItem>
                                <SelectItem value="grocery">Grocery Shopping</SelectItem>
                                <SelectItem value="prescription">Prescription Pickup</SelectItem>
                                <SelectItem value="returns">Returns/Exchanges</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Number of Stops</Label>
                            <Input
                              type="number"
                              min="1"
                              max="10"
                              value={formData.num_stops}
                              onChange={(e) => handleChange('num_stops', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Est. Spend Budget</Label>
                            <div className="relative mt-1">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">$</span>
                              <Input
                                type="number"
                                min="0"
                                value={formData.estimated_spend}
                                onChange={(e) => handleChange('estimated_spend', e.target.value)}
                                placeholder="0"
                                className="pl-7"
                              />
                            </div>
                          </div>
                        </div>
                        {formData.errand_type === 'grocery' && (
                          <div>
                            <Label className="text-xs">Shopping List / Items Needed</Label>
                            <Textarea
                              value={formData.shopping_list}
                              onChange={(e) => handleChange('shopping_list', e.target.value)}
                              placeholder="List items needed..."
                              rows={2}
                              className="mt-1"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* LAUNDRY DETAILS */}
                    {formData.service_type === 'laundry' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs">Number of Loads</Label>
                            <Input
                              type="number"
                              min="1"
                              max="20"
                              value={formData.num_loads}
                              onChange={(e) => handleChange('num_loads', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Service Type</Label>
                            <Select value={formData.laundry_service} onValueChange={(v) => handleChange('laundry_service', v)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="wash_dry_fold">Wash, Dry & Fold ($25/load)</SelectItem>
                                <SelectItem value="wash_only">Wash Only ($15/load)</SelectItem>
                                <SelectItem value="dry_only">Dry Only ($12/load)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Dry Clean Items</Label>
                            <Input
                              type="number"
                              min="0"
                              value={formData.dry_cleaning_items}
                              onChange={(e) => handleChange('dry_cleaning_items', e.target.value)}
                              placeholder="0"
                              className="mt-1"
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.include_pickup_delivery}
                              onCheckedChange={(checked) => handleChange('include_pickup_delivery', checked)}
                            />
                            <span className="text-sm">Include Pickup & Delivery (+$10)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <Checkbox
                              checked={formData.is_express_laundry}
                              onCheckedChange={(checked) => handleChange('is_express_laundry', checked)}
                            />
                            <span className="text-sm">Express/Same Day (+$15)</span>
                          </label>
                        </div>
                        <div>
                          <Label className="text-xs">Special Care Items (delicates, stains, etc.)</Label>
                          <Input
                            value={formData.special_care_items}
                            onChange={(e) => handleChange('special_care_items', e.target.value)}
                            placeholder="Any special care instructions..."
                            className="mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Schedule & Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  Schedule
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => handleChange('scheduled_date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1"
                      data-testid="phone-booking-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Time *</Label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => handleChange('scheduled_time', e.target.value)}
                      className="mt-1"
                      data-testid="phone-booking-time"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-xs">Estimated Duration (hours)</Label>
                  <Input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => handleChange('estimated_hours', e.target.value)}
                    className="mt-1 w-24"
                  />
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#D4AF37]" />
                  Pricing & Payment
                </h3>
                
                {/* Calculated Price Display */}
                {formData.service_type && calculatedPrice.total > 0 && (
                  <div className="mb-4 p-4 rounded-xl bg-gradient-to-r from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-[#D4AF37]" />
                        Calculated Total
                      </span>
                      <span className="text-2xl font-bold text-[#D4AF37]">${calculatedPrice.total.toFixed(2)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                      className="text-xs text-[var(--text-muted)] flex items-center gap-1 hover:text-[var(--text-primary)]"
                    >
                      {showPriceBreakdown ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {showPriceBreakdown ? 'Hide' : 'Show'} Price Breakdown
                    </button>
                    <AnimatePresence>
                      {showPriceBreakdown && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-3 pt-3 border-t border-[#D4AF37]/20 space-y-1"
                        >
                          {calculatedPrice.breakdown.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-[var(--text-muted)]">{item.label}</span>
                              <span className="font-medium">${item.amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <label className="flex items-center gap-2 mt-3 cursor-pointer">
                      <Checkbox
                        checked={formData.use_calculated_price}
                        onCheckedChange={(checked) => handleChange('use_calculated_price', checked)}
                      />
                      <span className="text-xs">Use calculated price</span>
                    </label>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Final Quoted Price</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.quoted_price}
                      onChange={(e) => {
                        handleChange('quoted_price', e.target.value);
                        handleChange('use_calculated_price', false);
                      }}
                      placeholder="0.00"
                      className="pl-7"
                      data-testid="phone-booking-price"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <Label className="text-xs">Payment Method</Label>
                  <div className="flex gap-2 mt-2">
                    {[
                      { id: 'pending', label: 'Pending', icon: Clock },
                      { id: 'cash', label: 'Cash', icon: Banknote },
                      { id: 'card', label: 'Card', icon: CreditCard }
                    ].map(method => (
                      <button
                        key={method.id}
                        type="button"
                        onClick={() => {
                          handleChange('payment_method', method.id);
                          if (method.id === 'card') {
                            setShowCardPayment(true);
                          } else {
                            setShowCardPayment(false);
                            setCardPermissionConfirmed(false);
                          }
                        }}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                          formData.payment_method === method.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-[var(--border)] text-[var(--text-muted)]'
                        }`}
                      >
                        <method.icon className="w-4 h-4" />
                        <span className="text-sm">{method.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Card Payment Over Phone Section */}
                <AnimatePresence>
                  {showCardPayment && formData.payment_method === 'card' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 p-4 rounded-xl border-2 border-amber-500/50 bg-amber-500/5"
                    >
                      <div className="flex items-start gap-3 mb-4">
                        <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-amber-600">Card Payment Over Phone</h4>
                          <p className="text-xs text-[var(--text-muted)] mt-1">
                            Read the verbal permission script to the customer before collecting card information.
                          </p>
                        </div>
                      </div>

                      {/* Verbal Permission Script */}
                      <div className="mb-4 p-3 rounded-lg bg-[var(--background)] border border-[var(--border)]">
                        <p className="text-xs font-medium text-amber-600 mb-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          READ THIS TO CUSTOMER:
                        </p>
                        <p className="text-sm italic text-[var(--text-primary)]">
                          "To process your payment of <strong>${formData.quoted_price || '0.00'}</strong> for {selectedService?.label || 'this service'}, 
                          I need to collect your card information over the phone. Do I have your verbal permission to charge this card? 
                          Your card information will be securely processed and not stored."
                        </p>
                      </div>

                      <label className="flex items-start gap-3 cursor-pointer mb-4 p-3 rounded-lg border border-green-500/30 bg-green-500/5">
                        <Checkbox
                          checked={cardPermissionConfirmed}
                          onCheckedChange={(checked) => {
                            setCardPermissionConfirmed(checked);
                            handleChange('verbal_permission_script_read', checked);
                          }}
                          className="mt-0.5"
                        />
                        <div>
                          <span className="text-sm font-medium text-green-600">Customer gave verbal permission</span>
                          <p className="text-xs text-[var(--text-muted)]">Check this box only after customer confirms "Yes"</p>
                        </div>
                      </label>

                      {cardPermissionConfirmed && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="space-y-3"
                        >
                          <div>
                            <Label className="text-xs">Cardholder Name</Label>
                            <Input
                              value={formData.card_holder_name}
                              onChange={(e) => handleChange('card_holder_name', e.target.value)}
                              placeholder="Name on card"
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Last 4 Digits of Card</Label>
                            <Input
                              value={formData.card_last_four}
                              onChange={(e) => handleChange('card_last_four', e.target.value.replace(/\D/g, '').slice(0, 4))}
                              placeholder="1234"
                              maxLength={4}
                              className="mt-1 w-24"
                            />
                            <p className="text-xs text-[var(--text-muted)] mt-1">
                              For record keeping only. A secure payment link will be emailed to the customer.
                            </p>
                          </div>
                          
                          {/* Email requirement notice */}
                          {!formData.customer_email && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                              <p className="text-xs text-amber-600 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                <span><strong>Customer email required</strong> to send payment link. Please add email above.</span>
                              </p>
                            </div>
                          )}
                          {formData.customer_email && (
                            <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                              <p className="text-xs text-green-600 flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" />
                                <span>Payment link will be sent to: <strong>{formData.customer_email}</strong></span>
                              </p>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Worker Assignment */}
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-[#D4AF37]" />
                Assign Worker (Optional)
              </h3>
              <Select 
                value={formData.assigned_worker_id || "unassigned"} 
                onValueChange={(v) => handleChange('assigned_worker_id', v === "unassigned" ? '' : v)}
              >
                <SelectTrigger className="w-full md:w-1/2">
                  <SelectValue placeholder="Select a worker to assign" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned (will be available for pickup)</SelectItem>
                  {availableWorkers.map(worker => (
                    <SelectItem key={worker.id} value={worker.id}>
                      {worker.full_name} {worker.rating ? `(${worker.rating}★)` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.service_type && availableWorkers.length === 0 && (
                <p className="text-xs text-amber-500 mt-2">
                  No verified workers available for {selectedService?.label}
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#D4AF37]" />
                Notes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Special Instructions (visible to worker)</Label>
                  <Textarea
                    value={formData.special_instructions}
                    onChange={(e) => handleChange('special_instructions', e.target.value)}
                    placeholder="Any special requests or instructions..."
                    rows={3}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Internal Notes (owner only)</Label>
                  <Textarea
                    value={formData.internal_notes}
                    onChange={(e) => handleChange('internal_notes', e.target.value)}
                    placeholder="Notes about the call, customer preferences, etc."
                    rows={3}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormData({
                  customer_name: '',
                  customer_phone: '',
                  customer_email: '',
                  service_type: '',
                  service_name: '',
                  pickup_address: '',
                  pickup_city: '',
                  pickup_zip: '',
                  delivery_address: '',
                  delivery_city: '',
                  delivery_zip: '',
                  scheduled_date: '',
                  scheduled_time: '',
                  estimated_hours: 1,
                  quoted_price: '',
                  payment_method: 'pending',
                  assigned_worker_id: '',
                  special_instructions: '',
                  internal_notes: ''
                })}
              >
                Clear Form
              </Button>
              <Button
                type="submit"
                className="btn-primary"
                disabled={loading}
                data-testid="create-phone-booking-btn"
              >
                {loading ? (
                  'Creating...'
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Booking
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Recent Phone Bookings Sidebar */}
        <div className="lg:col-span-1">
          <div className="card p-4">
            <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--text-muted)]" />
              Recent Phone Bookings
            </h3>
            
            {recentBookings.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] text-center py-8">
                No recent phone bookings
              </p>
            ) : (
              <div className="space-y-3">
                {recentBookings.map(booking => (
                  <div 
                    key={booking.id}
                    className="p-3 rounded-lg bg-[var(--background)] border border-[var(--border)]"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{booking.guest_name || booking.customer_name}</p>
                        <p className="text-xs text-[var(--text-muted)]">{booking.service_name}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        booking.status === 'completed' ? 'bg-green-500/20 text-green-500' :
                        booking.status === 'assigned' ? 'bg-blue-500/20 text-blue-500' :
                        'bg-amber-500/20 text-amber-500'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-xs text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(booking.scheduled_date).toLocaleDateString()}
                      </span>
                      {booking.total_amount > 0 && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          ${booking.total_amount}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Quick Tips */}
          <div className="card p-4 mt-4 bg-[#D4AF37]/5 border-[#D4AF37]/20">
            <h4 className="font-medium text-sm text-[#D4AF37] mb-3">Quick Tips</h4>
            <ul className="space-y-2 text-xs text-[var(--text-muted)]">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                Always confirm customer phone number by reading it back
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                Get full address including city and ZIP code
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                Quote price before creating booking
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                Add internal notes about customer preferences
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneBookingTab;
